import React, { useEffect } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Button,
  Grid,
} from "@mui/material";
import { 
  setSelectedFactoryFromPicker,
  getTraceabilityFacilitiesBySupplier 
} from "../reducers/factory.reducer";
import { 
  selectSelectedFactoryFromPicker,
  selectTraceabilityFacilitiesBySupplier 
} from "../selectors/factory.selector";
import { selectSupplierId } from "../selectors/login.selector";
import { useDispatch, useSelector } from "react-redux";

const PrefillFactoryInformationPicker = ({
  facilityPickerOpen,
  handleCloseFacilityPicker,
  handleFacilitySelect,
}) => {
  const dispatch = useDispatch();
  const selectedFacility = useSelector(selectSelectedFactoryFromPicker);
  const supplierId = useSelector(selectSupplierId);
  const traceabilityFacilities = useSelector(selectTraceabilityFacilitiesBySupplier);

  // Fetch traceability facilities when component mounts
  useEffect(() => {
    if (supplierId && facilityPickerOpen) {
      dispatch(getTraceabilityFacilitiesBySupplier({ supplierId }));
    }
  }, [dispatch, supplierId, facilityPickerOpen]);

  // Remove the useEffect that clears the selection - this was preventing re-selection
  // useEffect(() => {
  //   dispatch(setSelectedFactoryFromPicker(null));
  // }, []);

  return (
    <Box
      sx={{
        my: 3,
        p: 4,
        backgroundColor: "#f8f9fa",
        borderRadius: "8px",
        border: "2px dashed #e0e0e0",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: "#333333" }}>
          Pick from previous facility to fill
        </Typography>
        <Button
          onClick={handleCloseFacilityPicker}
          variant="outlined"
          size="small"
          sx={{
            color: "#666666",
            borderColor: "#666666",
            textTransform: "none",
            fontWeight: 500,
            padding: "4px 8px",
            borderRadius: "4px",
            fontSize: "12px",
            "&:hover": {
              backgroundColor: "#f8f9fa",
              borderColor: "#333333",
            },
          }}
        >
          ✕
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Typography
            variant="subtitle1"
            sx={{ mb: 2, fontWeight: 600, color: "#333333" }}
          >
            Facility
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={selectedFacility?.factoryId || ""}
              onChange={(e) => {
                const selectedId = e.target.value;
                console.log("[Factory Picker] Selected ID:", selectedId);
                console.log(
                  "[Factory Picker] Available traceability facilities:",
                  traceabilityFacilities.map((f) => ({
                    factoryId: f.factoryId,
                    name: f.name,
                  }))
                );
                const facility = traceabilityFacilities.find(
                  (f) => f.factoryId === selectedId
                );
                console.log("[Factory Picker] Found facility:", facility);
                if (facility) {
                  dispatch(setSelectedFactoryFromPicker(facility));
                }
              }}
              displayEmpty
              sx={{
                "& .MuiSelect-select": {
                  padding: "12px 14px",
                  fontSize: "14px",
                },
              }}
            >
              <MenuItem value="">
                <em>Select a facility</em>
              </MenuItem>
              {traceabilityFacilities.map((facility) => (
                <MenuItem key={facility.factoryId} value={facility.factoryId}>
                  {facility.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={8}>
          {selectedFacility && (
            <Box
              sx={{
                p: 3,
                backgroundColor: "#ffffff",
                borderRadius: "6px",
                border: "1px solid #e0e0e0",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ mb: 2, fontWeight: 600, color: "#333333" }}
              >
                Facility Details
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography
                    variant="body2"
                    sx={{ color: "#666666", fontWeight: 500 }}
                  >
                    Name:
                  </Typography>
                  <Typography variant="body1" sx={{ color: "#333333" }}>
                    {selectedFacility?.name || ""}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="body2"
                    sx={{ color: "#666666", fontWeight: 500 }}
                  >
                    Street Address:
                  </Typography>
                  <Typography variant="body1" sx={{ color: "#333333" }}>
                    {selectedFacility?.metadata?.streetAddress || selectedFacility?.metadata?.address || ""}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="body2"
                    sx={{ color: "#666666", fontWeight: 500 }}
                  >
                    City:
                  </Typography>
                  <Typography variant="body1" sx={{ color: "#333333" }}>
                    {selectedFacility?.metadata?.city || ""}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="body2"
                    sx={{ color: "#666666", fontWeight: 500 }}
                  >
                    Country:
                  </Typography>
                  <Typography variant="body1" sx={{ color: "#333333" }}>
                    {selectedFacility?.metadata?.country || ""}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="body2"
                    sx={{ color: "#666666", fontWeight: 500 }}
                  >
                    Parent Company - Name:
                  </Typography>
                  <Typography variant="body1" sx={{ color: "#333333" }}>
                    {selectedFacility?.supplierName || selectedFacility?.metadata?.parentCompanyName || ""}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="body2"
                    sx={{ color: "#666666", fontWeight: 500 }}
                  >
                    Parent Company - Street:
                  </Typography>
                  <Typography variant="body1" sx={{ color: "#333333" }}>
                    {selectedFacility?.metadata?.parentCompanyStreet || ""}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="body2"
                    sx={{ color: "#666666", fontWeight: 500 }}
                  >
                    Parent Company - City:
                  </Typography>
                  <Typography variant="body1" sx={{ color: "#333333" }}>
                    {selectedFacility?.metadata?.parentCompanyCity || ""}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="body2"
                    sx={{ color: "#666666", fontWeight: 500 }}
                  >
                    Parent Company - Country:
                  </Typography>
                  <Typography variant="body1" sx={{ color: "#333333" }}>
                    {selectedFacility?.metadata?.parentCompanyCountry || ""}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="body2"
                    sx={{ color: "#666666", fontWeight: 500 }}
                  >
                    Worldly ID:
                  </Typography>
                  <Typography variant="body1" sx={{ color: "#333333" }}>
                    {selectedFacility?.metadata?.worldlyId || selectedFacility?.metadata?.higgId || ""}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="body2"
                    sx={{ color: "#666666", fontWeight: 500 }}
                  >
                    ZDHC ID:
                  </Typography>
                  <Typography variant="body1" sx={{ color: "#333333" }}>
                    {selectedFacility?.metadata?.zdhcId || ""}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="body2"
                    sx={{ color: "#666666", fontWeight: 500 }}
                  >
                    TE-ID:
                  </Typography>
                  <Typography variant="body1" sx={{ color: "#333333" }}>
                    {selectedFacility?.metadata?.teId || ""}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    variant="body2"
                    sx={{ color: "#666666", fontWeight: 500 }}
                  >
                    OS Hub ID:
                  </Typography>
                  <Typography variant="body1" sx={{ color: "#333333" }}>
                    {selectedFacility?.metadata?.osHubId || ""}
                  </Typography>
                </Grid>
              </Grid>

              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                <Button
                  onClick={() =>
                    selectedFacility && handleFacilitySelect(selectedFacility)
                  }
                  variant="contained"
                  disabled={!selectedFacility}
                  sx={{
                    backgroundColor: "#4f46e5",
                    color: "#ffffff",
                    textTransform: "none",
                    fontWeight: 500,
                    padding: "8px 16px",
                    borderRadius: "6px",
                    fontSize: "14px",
                    "&:hover": {
                      backgroundColor: "#4338ca",
                    },
                    "&:disabled": {
                      backgroundColor: "#d1d5db",
                      color: "#9ca3af",
                    },
                  }}
                >
                  Confirm Fill the information below with this information
                </Button>
              </Box>
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default PrefillFactoryInformationPicker;
